// Thin fetch wrapper. Workers API base = same origin (workspace.l7.gg).
// Session cookie (__Host-l7ws_session) is HttpOnly + SameSite=Strict; the browser
// includes it automatically on same-origin requests.

import type { ApiError } from "@/types";

export class HttpError extends Error {
  constructor(public status: number, public body: ApiError) {
    super(body.message);
  }
}

export async function http<T>(input: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(input, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "Origin": window.location.origin,
      ...(init.headers || {}),
    },
    ...init,
  });
  if (!res.ok) {
    let body: ApiError;
    try { body = await res.json() as ApiError; }
    catch { body = { code: "unknown", message: res.statusText }; }
    throw new HttpError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const Endpoints = {
  // Auth (plan §4)
  auth_login:     "/auth/login",
  auth_callback:  "/auth/callback",
  auth_logout:    "/auth/logout",
  auth_me:        "/api/me",
  auth_session:   "/api/session",

  // Mail (Worker proxies + JMAP direct)
  mail_health:    "/api/mail/health",
  mail_telemetry: "/api/mail/telemetry",      // ?rfc822_message_id=…
  mail_search_save:"/api/mail/searches",

  // Support
  support_tickets:    "/api/support/tickets",
  support_ticket:     (id: string) => `/api/support/tickets/${id}`,
  support_messages:   (id: string) => `/api/support/tickets/${id}/messages`,
  support_forward:    (id: string) => `/api/support/tickets/${id}/forward`,
  support_activity:   (id: string) => `/api/support/tickets/${id}/activity`,
  support_inboxes:    "/api/support/inboxes",
  support_agents:     "/api/support/agents",
  support_tags:       "/api/support/tags",
  support_search:     "/api/support/search",

  // Files
  files_list:         "/api/files",
  files_one:          (id: string) => `/api/files/${id}`,
  files_upload_init:  "/api/files/upload/init",
  files_upload_done:  "/api/files/upload/complete",
  files_share:        (id: string) => `/api/files/${id}/share`,
  files_versions:     (id: string) => `/api/files/${id}/versions`,
  files_search:       "/api/files/search",
  folders:            "/api/folders",
  folder_one:         (id: string) => `/api/folders/${id}`,

  // Calendar
  calendars:          "/api/calendars",
  cal_events:         "/api/events",
  cal_event_one:      (id: string) => `/api/events/${id}`,
  cal_rsvp:           (id: string) => `/api/events/${id}/rsvp`,
  cal_attendees:      (id: string) => `/api/events/${id}/attendees`,
  cal_nl_parse:       "/api/calendar/nl-parse",
  cal_conflict_check: "/api/calendar/conflict-check",

  // Booking links (host CRUD + public booking surface)
  booking_types:      "/api/me/booking-types",
  booking_type_one:   (id: string) => `/api/me/booking-types/${id}`,
  my_bookings:        "/api/me/bookings",
  my_booking_cancel:  (id: string) => `/api/me/bookings/${id}/cancel`,
  book_info:          (slug: string) => `/api/book/${slug}`,
  book_slots:         (slug: string) => `/api/book/${slug}/slots`,
  book_cancel:        (token: string) => `/api/book/cancel/${token}`,

  // Meetings
  meet_room:          (id: string) => `/api/meetings/rooms/${id}`,
  meet_join:          (id: string) => `/api/meetings/rooms/${id}/join`,
  meet_leave:         (id: string) => `/api/meetings/rooms/${id}/leave`,
  meet_chat:          (id: string) => `/api/meetings/rooms/${id}/chat`,
  meet_record_toggle: (id: string) => `/api/meetings/rooms/${id}/recording`,
  recordings:         "/api/meetings/recordings",
  recording_one:      (id: string) => `/api/meetings/recordings/${id}`,

  // Attachment preview (different origin per §3.8)
  preview_token:      "/api/preview/token",   // POST { attachment_id } -> { token, preview_url }

  // Mailtrap webhook ingestion (Worker handles this server-side; UI never calls it)
  // /api/mailtrap/webhook
};
